import type { AssetRow } from '../../data/fleet.ts'
import type { PlanJob } from '../../data/plan.ts'
import { dayOfMonth, weekdayShort } from '../../data/planUi.ts'
import { isoDayDiff, type Proposal, type ProposalReason, type RotationInputs, type Unfilled } from '../../data/rotation.ts'

/** Pure screen logic of the week board (M18): which jobs still take people, a calm colour per kind of task, and the words of the rotation assistant. */
type T = (k: string, o?: Record<string, unknown>) => string

/** M23 item 26: the days the week grid shows. From `sm:` the seven days of the week; on a phone a three-day window around the day chosen (hier / aujourd'hui / demain),
 *  which ‹ › move by a day, so tomorrow is never five columns off-screen. The week itself (the overview, the sheet, the rotation) stays the seven days. */
export function weekWindow(week: readonly string[], date: string, wide: boolean, add: (iso: string, n: number) => string): string[] {
  if (wide) return [...week]
  const centre = week.includes(date) ? date : week[0]!
  return [add(centre, -1), centre, add(centre, 1)]
}

/** M23 item 27: "CÉ" for "Camille Équipe (fictif)" on a compact card. ONE rule for the whole app (review of M23: the board said "JP" for "Jean Pierre Dupont"
 *  while the sheet pinned in the shed and the live map said "JD"): the live map's helper, the first and the last word, what is in brackets left out. */
export { initials } from '../../data/liveMap.ts'

/** M23 item 29: the machine picker's order. The machines whose TYPE was used for this kind of task before (from the device's own jobs: the lines of every job of that
 *  task type and the type of the machine on each) come first, then the rest; each group by tag. A machine with no type is never "usual". No task type (a new job from a
 *  template): nothing usual, everything by tag. */
export function machinesForTask<M extends Pick<AssetRow, 'id' | 'readable_tag' | 'asset_type_code'>>(
  machines: readonly M[], jobs: ReadonlyArray<Pick<PlanJob, 'task_type_code' | 'crew'>>, taskTypeCode: string | null | undefined, typeOf: (assetId: string) => string | null | undefined,
): { usual: M[]; others: M[] } {
  const byTag = (a: M, b: M) => a.readable_tag.localeCompare(b.readable_tag, 'fr', { numeric: true })
  const types = new Set<string>()
  if (taskTypeCode) for (const j of jobs) if (j.task_type_code === taskTypeCode) for (const a of j.crew) { const ty = a.asset_id ? typeOf(a.asset_id) : null; if (ty) types.add(ty) }
  const usual = machines.filter((m) => !!m.asset_type_code && types.has(m.asset_type_code)).sort(byTag)
  const others = machines.filter((m) => !(m.asset_type_code && types.has(m.asset_type_code))).sort(byTag)
  return { usual, others }
}
/** The picker's search: tag, make, model or the type's label, accents and case aside. */
export function matchMachine(q: string, m: Pick<AssetRow, 'readable_tag' | 'make' | 'model'>, typeLabel: string): boolean {
  const plain = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  const needle = plain(q.trim())
  return !needle || plain([m.readable_tag, m.make ?? '', m.model ?? '', typeLabel].join(' ')).includes(needle)
}

export const groupName = (t: T, n: number): string => (n === 1 ? t('plan.groupFirst') : n === 2 ? t('plan.groupSecond') : t('plan.groupN', { n }))

/** The states of a job that people can still be sent to (the board's live jobs). */
export const LIVE_STATES: ReadonlySet<string> = new Set(['planned', 'in_progress', 'paused', 'partially_done'])
/** Seats still empty on a job: a job that names no crew size takes one person. */
export const seatsLeft = (job: Pick<PlanJob, 'required_crew' | 'crew'>): number => Math.max(0, (job.required_crew ?? 1) - job.crew.length)
export const needsPeople = (job: Pick<PlanJob, 'required_crew' | 'crew' | 'state'>): boolean => LIVE_STATES.has(job.state) && seatsLeft(job) > 0
/** The live jobs of a day for the "Travail" list: the ones that still need people first, each part in the board's own order. */
export const jobsToStaffFirst = (jobs: readonly PlanJob[]): PlanJob[] => {
  const live = jobs.filter((j) => LIVE_STATES.has(j.state))
  return [...live.filter((j) => needsPeople(j)), ...live.filter((j) => !needsPeople(j))]
}

/** Eight calm palettes (Tailwind's own colours, light and dark): a kind of task always gets the same one. A picture, never a rule. */
export const TASK_PALETTES = [
  'border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-50',
  'border-sky-300 bg-sky-50 text-sky-950 dark:border-sky-700 dark:bg-sky-950/60 dark:text-sky-50',
  'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-700 dark:bg-amber-950/60 dark:text-amber-50',
  'border-lime-300 bg-lime-50 text-lime-950 dark:border-lime-700 dark:bg-lime-950/60 dark:text-lime-50',
  'border-teal-300 bg-teal-50 text-teal-950 dark:border-teal-700 dark:bg-teal-950/60 dark:text-teal-50',
  'border-violet-300 bg-violet-50 text-violet-950 dark:border-violet-700 dark:bg-violet-950/60 dark:text-violet-50',
  'border-rose-300 bg-rose-50 text-rose-950 dark:border-rose-700 dark:bg-rose-950/60 dark:text-rose-50',
  'border-orange-300 bg-orange-50 text-orange-950 dark:border-orange-700 dark:bg-orange-950/60 dark:text-orange-50',
] as const
/** FNV-1a of the task type code, so the colour does not depend on the order the types were created in. */
export function taskPalette(code: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < code.length; i++) { h ^= code.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0 }
  return TASK_PALETTES[h % TASK_PALETTES.length]!
}

export const HISTORY_WEEKS = [4, 8, 12] as const

/** What the period before the week alone says about a person and a kind of task (the server's `history` rows, summed as the rule sums them): how many jobs, the last day. */
export interface PastTask { n: number; last: string | null }
export function pastTask(history: RotationInputs['history'], personId: string, taskType: string): PastTask {
  let n = 0, last: string | null = null
  for (const h of history) {
    if (h.person_id !== personId || h.task_type_code !== taskType) continue
    n += h.n
    if (last === null || h.last_date > last) last = h.last_date
  }
  return { n, last }
}

/** Why a person is proposed, in words: the reasons of the rule, in its own order. The rule's own count (`taskCount`) and last time (`lastDaysAgo`) also hold the lines only
 *  planned or proposed (from today on), so the words keep the two apart: "N fois en 8 semaines, la dernière il y a …" is the history only (before today, counted back from
 *  today), and what is only planned or proposed is said on its own ("déjà N fois prévu ou proposé"). Planned work is never presented as done. */
export function reasonText(t: T, p: Pick<Proposal, 'reason' | 'person_id' | 'task_type_code' | 'local_date'>, history: RotationInputs['history'], weeks: number, today: string): string {
  const r: ProposalReason = p.reason
  const past = pastTask(history, p.person_id, p.task_type_code)
  const thisWeek = Math.max(0, r.taskCount - past.n)
  const parts: string[] = []
  if (r.partial) parts.push(t('plan.rotation.why.partial'))
  if (past.n === 0) parts.push(t('plan.rotation.why.never', { count: weeks }))
  else {
    const times = t('plan.rotation.why.times', { count: past.n, weeks })
    const ago = past.last === null ? null : isoDayDiff(past.last, today)                            // the history ends before today: always at least one day
    parts.push(ago !== null && ago > 0 ? t('plan.rotation.why.timesLast', { times, last: t('plan.rotation.why.last', { count: ago }) }) : times)
  }
  if (thisWeek > 0) parts.push(t('plan.rotation.why.thisWeek', { count: thisWeek }))
  parts.push(r.weekLoad === 0 ? t('plan.rotation.why.loadNone') : t('plan.rotation.why.load', { count: r.weekLoad }))
  if (r.machine === 'template_type') parts.push(t('plan.rotation.why.machineType'))
  else if (r.machine === 'usual_for_task') parts.push(t('plan.rotation.why.machineUsual', { count: r.machineUses }))
  else if (r.machine === 'none_free') parts.push(t('plan.rotation.why.noneFree'))
  else if (r.machine === 'none_allowed') parts.push(t('plan.rotation.why.noneAllowed'))
  return parts.join(' · ')
}

/** "mar. 20 · Tonte : 1 place sans personne (2 absents, 1 déjà pris)". */
export function unfilledText(t: T, u: Unfilled, job: string, lang: string): string {
  const why = [
    u.off > 0 ? t('plan.rotation.unfilled.off', { count: u.off }) : '',
    u.busy > 0 ? t('plan.rotation.unfilled.busy', { count: u.busy }) : '',
    u.unqualified > 0 ? t('plan.rotation.unfilled.unqualified', { count: u.unqualified }) : '',
    u.no_machine > 0 ? t('plan.rotation.unfilled.noMachine', { count: u.no_machine }) : '',
  ].filter(Boolean).join(', ')
  const day = `${weekdayShort(u.local_date, lang)} ${dayOfMonth(u.local_date)}`
  const seats = t('plan.rotation.unfilled.seats', { count: u.missing })
  return why ? t('plan.rotation.unfilled.lineWhy', { day, job, seats, why }) : t('plan.rotation.unfilled.line', { day, job, seats })
}
